/**
 * Lo único del negocio que se guarda en el celular: el trabajo del día del técnico.
 *
 * ── Por qué es una excepción, y por qué es estrecha ──
 *
 * La regla del sistema es que los datos del negocio NUNCA se guardan (ver `public/sw.js`):
 * una pantalla que enseña la cartera de ayer hace cobrar mal. Esa regla sigue en pie y el
 * service worker sigue sin tocar `/api`.
 *
 * Esto es una excepción decidida a propósito, y lo que la hace aceptable es dónde para:
 *
 *   · Se guarda UNA sola cosa: la lista de trabajos del técnico. Ni clientes, ni cartera,
 *     ni precios, ni nada que se cobre.
 *   · Se guarda para VER, nunca para escribir. Sin señal la pantalla no deja guardar y lo
 *     dice; no hay cola de cosas pendientes que después se suban y se pisen entre sí.
 *   · Se guarda CUÁNDO se trajo, y la pantalla lo enseña. El técnico siempre sabe si está
 *     mirando algo de ahora o de hace tres horas. Un dato viejo que se presenta como viejo
 *     no engaña a nadie; el problema es el que se presenta como al día.
 *   · Se borra al cerrar sesión. El celular de un técnico se pierde o se vende.
 *
 * El motivo por el que existe: el técnico está en una vereda de Caloto con la dirección del
 * cliente en la pantalla. Quedarse sin poder leer a dónde iba, por no tener señal justo ahí,
 * es el momento exacto en que una herramienta deja de servir.
 */

const LLAVE = 'linkus_tecnico_mi_dia';

/**
 * Guarda lo que acaba de llegar del servidor.
 *
 * Si el celular no deja guardar —modo privado, memoria llena— no pasa nada: la próxima vez
 * que haya señal se pide de nuevo. Por eso va envuelto y no revienta la pantalla.
 */
export function guardarElDia(datos) {
  try {
    localStorage.setItem(LLAVE, JSON.stringify(datos));
  } catch (e) { /* sin memoria para guardar: se seguirá pidiendo al servidor */ }
}

/**
 * Lo último que se alcanzó a traer, o `null` si no hay nada.
 *
 * @returns {{traido_en:string, tickets:Array}|null}
 */
export function leerElDia() {
  try {
    const crudo = localStorage.getItem(LLAVE);
    if (!crudo) return null;
    const datos = JSON.parse(crudo);
    return Array.isArray(datos?.tickets) ? datos : null;
  } catch (e) {
    return null;
  }
}

/** Al cerrar sesión no queda nada del negocio en el teléfono. */
export function olvidarElDia() {
  try { localStorage.removeItem(LLAVE); } catch (e) { /* nada que borrar */ }
}

/**
 * «hace 5 minutos», «hace 2 horas» — para poder decir de cuándo es lo que se está viendo.
 *
 * Se dice en tiempo transcurrido y no en hora exacta a propósito: «8:14 a.m.» obliga a mirar
 * el reloj y restar, y lo que importa no es la hora sino si está reciente.
 */
export function haceCuanto(cuando) {
  const minutos = Math.floor((Date.now() - new Date(cuando).getTime()) / 60000);
  if (!Number.isFinite(minutos) || minutos < 0) return '';
  if (minutos < 1) return 'hace un momento';
  if (minutos < 60) return `hace ${minutos} ${minutos === 1 ? 'minuto' : 'minutos'}`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `hace ${horas} ${horas === 1 ? 'hora' : 'horas'}`;
  const dias = Math.floor(horas / 24);
  return `hace ${dias} ${dias === 1 ? 'día' : 'días'}`;
}
